import {
  familyStatus,
  familyChildApprove,
  familyChildReject,
  familyChildDelete,
  familyChildToggleBooking,
  familyChildCreate,
  familyBookings,
  walletSummary,
  setChildHoliday,
  deleteChildHoliday,
  getLearningDashboard,
  getLearningTimeline,
  submitLearningPlacement,
} from '../../util/api';

// ==========================================
// ACTION TYPES
// ==========================================
export const FETCH_FAMILY_REQUEST = 'app/FamilyDashboardPage/FETCH_FAMILY_REQUEST';
export const FETCH_FAMILY_SUCCESS = 'app/FamilyDashboardPage/FETCH_FAMILY_SUCCESS';
export const FETCH_FAMILY_ERROR = 'app/FamilyDashboardPage/FETCH_FAMILY_ERROR';

export const FETCH_BOOKINGS_REQUEST = 'app/FamilyDashboardPage/FETCH_BOOKINGS_REQUEST';
export const FETCH_BOOKINGS_SUCCESS = 'app/FamilyDashboardPage/FETCH_BOOKINGS_SUCCESS';
export const FETCH_BOOKINGS_ERROR = 'app/FamilyDashboardPage/FETCH_BOOKINGS_ERROR';

export const FETCH_WALLET_REQUEST = 'app/FamilyDashboardPage/FETCH_WALLET_REQUEST';
export const FETCH_WALLET_SUCCESS = 'app/FamilyDashboardPage/FETCH_WALLET_SUCCESS';
export const FETCH_WALLET_ERROR = 'app/FamilyDashboardPage/FETCH_WALLET_ERROR';

export const FAMILY_ACTION_REQUEST = 'app/FamilyDashboardPage/FAMILY_ACTION_REQUEST';
export const FAMILY_ACTION_SUCCESS = 'app/FamilyDashboardPage/FAMILY_ACTION_SUCCESS';
export const FAMILY_ACTION_ERROR = 'app/FamilyDashboardPage/FAMILY_ACTION_ERROR';

export const FETCH_LEARNING_DASHBOARD_REQUEST = 'app/FamilyDashboardPage/FETCH_LEARNING_DASHBOARD_REQUEST';
export const FETCH_LEARNING_DASHBOARD_SUCCESS = 'app/FamilyDashboardPage/FETCH_LEARNING_DASHBOARD_SUCCESS';
export const FETCH_LEARNING_DASHBOARD_ERROR = 'app/FamilyDashboardPage/FETCH_LEARNING_DASHBOARD_ERROR';

export const SUBMIT_LEARNING_PLACEMENT_REQUEST = 'app/FamilyDashboardPage/SUBMIT_LEARNING_PLACEMENT_REQUEST';
export const SUBMIT_LEARNING_PLACEMENT_SUCCESS = 'app/FamilyDashboardPage/SUBMIT_LEARNING_PLACEMENT_SUCCESS';
export const SUBMIT_LEARNING_PLACEMENT_ERROR = 'app/FamilyDashboardPage/SUBMIT_LEARNING_PLACEMENT_ERROR';

export const FETCH_LEARNING_TIMELINE_REQUEST = 'app/FamilyDashboardPage/FETCH_LEARNING_TIMELINE_REQUEST';
export const FETCH_LEARNING_TIMELINE_SUCCESS = 'app/FamilyDashboardPage/FETCH_LEARNING_TIMELINE_SUCCESS';
export const FETCH_LEARNING_TIMELINE_ERROR = 'app/FamilyDashboardPage/FETCH_LEARNING_TIMELINE_ERROR';

// ==========================================
// REDUCER
// ==========================================
const initialState = {
  familyData: null,
  bookings: [],
  walletData: null,
  fetchFamilyInProgress: false,
  fetchFamilyError: null,
  fetchBookingsInProgress: false,
  fetchBookingsError: null,
  fetchWalletInProgress: false,
  fetchWalletError: null,
  actionInProgress: false,
  actionError: null,
  learningData: null,
  fetchLearningInProgress: false,
  fetchLearningError: null,
  submitPlacementInProgress: false,
  submitPlacementError: null,
  timelineReports: [],
  timelinePage: 1,
  timelineHasMore: false,
  fetchTimelineInProgress: false,
  fetchTimelineError: null,
};

export default function familyDashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FAMILY_REQUEST:
      return { ...state, fetchFamilyInProgress: true, fetchFamilyError: null };
    case FETCH_FAMILY_SUCCESS:
      return { ...state, fetchFamilyInProgress: false, familyData: payload };
    case FETCH_FAMILY_ERROR:
      return { ...state, fetchFamilyInProgress: false, fetchFamilyError: payload };

    case FETCH_BOOKINGS_REQUEST:
      return { ...state, fetchBookingsInProgress: true, fetchBookingsError: null };
    case FETCH_BOOKINGS_SUCCESS:
      return { ...state, fetchBookingsInProgress: false, bookings: payload };
    case FETCH_BOOKINGS_ERROR:
      return { ...state, fetchBookingsInProgress: false, fetchBookingsError: payload };

    case FETCH_WALLET_REQUEST:
      return { ...state, fetchWalletInProgress: true, fetchWalletError: null };
    case FETCH_WALLET_SUCCESS:
      return { ...state, fetchWalletInProgress: false, walletData: payload };
    case FETCH_WALLET_ERROR:
      return { ...state, fetchWalletInProgress: false, fetchWalletError: payload };

    case FAMILY_ACTION_REQUEST:
      return { ...state, actionInProgress: true, actionError: null };
    case FAMILY_ACTION_SUCCESS:
      return { ...state, actionInProgress: false };
    case FAMILY_ACTION_ERROR:
      return { ...state, actionInProgress: false, actionError: payload };

    case FETCH_LEARNING_DASHBOARD_REQUEST:
      return { ...state, fetchLearningInProgress: true, fetchLearningError: null };
    case FETCH_LEARNING_DASHBOARD_SUCCESS: {
      const recentReports = payload?.recentReports || [];
      return {
        ...state,
        fetchLearningInProgress: false,
        learningData: payload,
        timelineReports: recentReports,
        timelinePage: 1,
        timelineHasMore: recentReports.length >= 5,
      };
    }
    case FETCH_LEARNING_DASHBOARD_ERROR:
      return { ...state, fetchLearningInProgress: false, fetchLearningError: payload };

    case FETCH_LEARNING_TIMELINE_REQUEST:
      return { ...state, fetchTimelineInProgress: true, fetchTimelineError: null };
    case FETCH_LEARNING_TIMELINE_SUCCESS: {
      const newReports = payload?.reports || [];
      const existingIds = new Set(state.timelineReports.map(r => String(r._id || r.id)));
      const filtered = newReports.filter(r => !existingIds.has(String(r._id || r.id)));
      return {
        ...state,
        fetchTimelineInProgress: false,
        timelineReports: [...state.timelineReports, ...filtered],
        timelinePage: payload?.page || state.timelinePage + 1,
        timelineHasMore: Boolean(payload?.hasMore),
      };
    }
    case FETCH_LEARNING_TIMELINE_ERROR:
      return { ...state, fetchTimelineInProgress: false, fetchTimelineError: payload };

    case SUBMIT_LEARNING_PLACEMENT_REQUEST:
      return { ...state, submitPlacementInProgress: true, submitPlacementError: null };
    case SUBMIT_LEARNING_PLACEMENT_SUCCESS:
      return { ...state, submitPlacementInProgress: false };
    case SUBMIT_LEARNING_PLACEMENT_ERROR:
      return { ...state, submitPlacementInProgress: false, submitPlacementError: payload };

    default:
      return state;
  }
}

// ==========================================
// ACTION CREATORS
// ==========================================
export const fetchFamilyRequest = () => ({ type: FETCH_FAMILY_REQUEST });
export const fetchFamilySuccess = data => ({ type: FETCH_FAMILY_SUCCESS, payload: data });
export const fetchFamilyError = error => ({ type: FETCH_FAMILY_ERROR, payload: error });

export const fetchBookingsRequest = () => ({ type: FETCH_BOOKINGS_REQUEST });
export const fetchBookingsSuccess = data => ({ type: FETCH_BOOKINGS_SUCCESS, payload: data });
export const fetchBookingsError = error => ({ type: FETCH_BOOKINGS_ERROR, payload: error });

export const fetchWalletRequest = () => ({ type: FETCH_WALLET_REQUEST });
export const fetchWalletSuccess = data => ({ type: FETCH_WALLET_SUCCESS, payload: data });
export const fetchWalletError = error => ({ type: FETCH_WALLET_ERROR, payload: error });

export const familyActionRequest = () => ({ type: FAMILY_ACTION_REQUEST });
export const familyActionSuccess = () => ({ type: FAMILY_ACTION_SUCCESS });
export const familyActionError = error => ({ type: FAMILY_ACTION_ERROR, payload: error });

export const fetchLearningDashboardRequest = () => ({ type: FETCH_LEARNING_DASHBOARD_REQUEST });
export const fetchLearningDashboardSuccess = data => ({ type: FETCH_LEARNING_DASHBOARD_SUCCESS, payload: data });
export const fetchLearningDashboardError = error => ({ type: FETCH_LEARNING_DASHBOARD_ERROR, payload: error });

export const submitLearningPlacementRequest = () => ({ type: SUBMIT_LEARNING_PLACEMENT_REQUEST });
export const submitLearningPlacementSuccess = data => ({ type: SUBMIT_LEARNING_PLACEMENT_SUCCESS, payload: data });
export const submitLearningPlacementError = error => ({ type: SUBMIT_LEARNING_PLACEMENT_ERROR, payload: error });

export const fetchLearningTimelineRequest = () => ({ type: FETCH_LEARNING_TIMELINE_REQUEST });
export const fetchLearningTimelineSuccess = data => ({ type: FETCH_LEARNING_TIMELINE_SUCCESS, payload: data });
export const fetchLearningTimelineError = error => ({ type: FETCH_LEARNING_TIMELINE_ERROR, payload: error });

// ==========================================
// THUNKS
// ==========================================

export const getFamilyStatus = userId => dispatch => {
  dispatch(fetchFamilyRequest());
  return familyStatus({ userId })
    .then(res => {
      if (res?.success) {
        dispatch(fetchFamilySuccess(res.data));
        return res.data;
      }
      throw new Error(res?.error || 'Failed to fetch family status');
    })
    .catch(err => {
      dispatch(fetchFamilyError(err.message));
      throw err;
    });
};

export const getFamilyBookings = parentUserId => dispatch => {
  dispatch(fetchBookingsRequest());
  return familyBookings({ parentUserId })
    .then(res => {
      if (res?.success) {
        dispatch(fetchBookingsSuccess(res.data));
        return res.data;
      }
      throw new Error(res?.error || 'Failed to fetch family bookings');
    })
    .catch(err => {
      dispatch(fetchBookingsError(err.message));
      throw err;
    });
};

export const approveChildRequest = (parentUserId, childUserId) => dispatch => {
  dispatch(familyActionRequest());
  return familyChildApprove({
    parentUserId,
    childUserId,
    legalGuardianConfirmed: true,
    consentGiven: true,
  })
    .then(res => {
      if (res?.success) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(parentUserId));
      }
      throw new Error(res?.error || 'Failed to approve request');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const rejectChildRequest = (parentUserId, childUserId) => dispatch => {
  dispatch(familyActionRequest());
  return familyChildReject({ parentUserId, childUserId })
    .then(res => {
      if (res?.success) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(parentUserId));
      }
      throw new Error(res?.error || 'Failed to reject request');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const deleteChildRequest = (parentUserId, childUserId) => dispatch => {
  dispatch(familyActionRequest());
  return familyChildDelete({ parentUserId, childUserId })
    .then(res => {
      if (res?.success) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(parentUserId));
      }
      throw new Error(res?.error || 'Failed to remove child');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const toggleChildBookingPermission = (parentUserId, childUserId, enabled) => dispatch => {
  dispatch(familyActionRequest());
  return familyChildToggleBooking({ parentUserId, childUserId, enabled })
    .then(res => {
      if (res?.success) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(parentUserId));
      }
      throw new Error(res?.error || 'Failed to update booking permission');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const createChildProfile = (parentUserId, childData) => dispatch => {
  dispatch(familyActionRequest());
  return familyChildCreate({ parentUserId, ...childData })
    .then(res => {
      if (res?.success) {
        dispatch(familyActionSuccess());
        dispatch(getFamilyStatus(parentUserId));
        return res.data;
      }
      throw new Error(res?.error || 'Failed to create child profile');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const uploadChildImage = ({ file }) => (dispatch, getState, sdk) => {
  const bodyParams = {
    image: file,
  };
  const queryParams = {
    expand: true,
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.images
    .upload(bodyParams, queryParams)
    .then(resp => {
      return resp?.data?.data || null;
    });
};

export const fetchLearningDashboard = ({ parentId, childId, userId }) => dispatch => {
  dispatch(fetchLearningDashboardRequest());
  return getLearningDashboard({ parentId, childId, userId })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && (data.success !== false)) {
        dispatch(fetchLearningDashboardSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch learning dashboard');
    })
    .catch(err => {
      dispatch(fetchLearningDashboardError(err.message));
      throw err;
    });
};

export const fetchLearningTimelineThunk = ({ parentId, childId, page = 1, perPage = 5 }) => dispatch => {
  dispatch(fetchLearningTimelineRequest());
  return getLearningTimeline({ parentId, childId, page, perPage })
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && (data.success !== false)) {
        dispatch(fetchLearningTimelineSuccess(data));
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to fetch learning timeline');
    })
    .catch(err => {
      dispatch(fetchLearningTimelineError(err.message));
      throw err;
    });
};

export const submitPlacementQuestionnaire = payload => dispatch => {
  dispatch(submitLearningPlacementRequest());
  return submitLearningPlacement(payload)
    .then(res => {
      const data = res?.data?.data || res?.data || res;
      if (data && (data.success !== false)) {
        dispatch(submitLearningPlacementSuccess(data));
        // Refresh learning dashboard if identifiers are present
        const pId = payload.parentId || payload.userId;
        const cId = payload.childId || payload.userId;
        if (pId || cId) {
          dispatch(fetchLearningDashboard({ parentId: pId, childId: cId, userId: payload.userId }));
        }
        return data;
      }
      throw new Error(data?.error || data?.message || 'Failed to submit placement');
    })
    .catch(err => {
      dispatch(submitLearningPlacementError(err.message));
      throw err;
    });
};

export const fetchWalletSummary = userId => dispatch => {
  dispatch(fetchWalletRequest());
  return walletSummary({ userId })
    .then(res => {
      const data = res?.data || res;
      if (data && data.success !== false) {
        const payload = data?.data || data;
        dispatch(fetchWalletSuccess(payload));
        return payload;
      }
      throw new Error(data?.error || 'Failed to fetch wallet summary');
    })
    .catch(err => {
      dispatch(fetchWalletError(err.message));
      throw err;
    });
};

export const setChildHolidayThunk = payload => dispatch => {
  dispatch(familyActionRequest());
  return setChildHoliday(payload)
    .then(res => {
      const data = res?.data || res;
      if (data && (data.success || data.ok)) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(payload.parentUserId));
      }
      throw new Error(data?.error || 'Failed to set child holiday');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const deleteChildHolidayThunk = payload => dispatch => {
  dispatch(familyActionRequest());
  return deleteChildHoliday(payload)
    .then(res => {
      const data = res?.data || res;
      if (data && (data.success || data.ok)) {
        dispatch(familyActionSuccess());
        return dispatch(getFamilyStatus(payload.parentUserId));
      }
      throw new Error(data?.error || 'Failed to delete child holiday');
    })
    .catch(err => {
      dispatch(familyActionError(err.message));
      throw err;
    });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const userId = getState().user.currentUser?.id?.uuid;
  if (!userId) {
    return Promise.resolve();
  }
  return Promise.all([
    dispatch(getFamilyStatus(userId)),
    dispatch(getFamilyBookings(userId)),
    dispatch(fetchWalletSummary(userId)),
    dispatch(fetchLearningDashboard({ parentId: userId, userId })),
  ]);
};
